import { Injectable } from '@angular/core';
import { toImmutableMap } from '@datachain/ui-sdk/common';

import dayjs from '../core/configs/dayjs';
import {
	IDcTaskResponse,
	ITaskTypeMappingResponse,
} from '../requesters/core/models/task.model';
import { TaskItemEntity } from '../ui/components/app-task-modal/app-task-item.entity';
import {
	ITaskListExtra,
	TaskListEntity,
} from '../ui/components/app-task-modal/app-task-list.entity';
import { DcCoreElementType, DcCoreElementTypesMapping } from './dc-core-types';
import { DxListsStorage } from './dx-lists.storage';
import { DcDxGrids } from './dx-types';
import { DcRouteUrlBuilder } from './utils/dc-route-url-builder';

@Injectable({
	providedIn: 'root',
})
export class TaskAdapter {
	public constructor(private readonly dxListsStorage: DxListsStorage) {}

	public generateTaskList(
		response: ReadonlyArray<IDcTaskResponse>,
		types: ReadonlyArray<ITaskTypeMappingResponse>
	): TaskListEntity {
		const columns = this.getTaskColumns();
		const availableTypesMapping = this.generateTaskTypes(types);
		const columnsTuples = response
			.reduce<Array<TaskItemEntity>>((acc, item) => {
				const temp = this.generateTaskItemEntity(item, availableTypesMapping);
				acc.push(temp);
				return acc;
			}, [])
			.sort((a, b) => {
				if (!a.executionDate) {
					throw new Error('Date is null!');
				}
				if (!b.executionDate) {
					throw new Error('Date is null!');
				}

				return b.executionDate.getTime() - a.executionDate.getTime();
			});
		return columns.setElements(toImmutableMap(columnsTuples, 'id'));
	}

	private generateTaskTypes(
		response: ReadonlyArray<ITaskTypeMappingResponse>
	): Map<string, { code: string; value: string }> {
		return new Map(
			response.map((r) => [
				r.code,
				{
					code: r.code,
					value: r.value,
				},
			])
		);
	}

	private parseProjectSourcePageUrl(projectId: number): string {
		return DcRouteUrlBuilder.buildEditPageUrl(projectId, undefined, undefined);
	}

	private generateTaskItemEntity(
		response: IDcTaskResponse,
		availableTypesMapping: Map<string, { code: string; value: string }>
	): TaskItemEntity {
		const elementType: DcCoreElementType = response.element_type
			? DcCoreElementTypesMapping[response.element_type]
			: DcCoreElementType.UNKNOWN;
		return TaskItemEntity.build({
			id: response.id,
			taskId: response.job_id,
			taskType: response.type ? availableTypesMapping.get(response.type) : null,
			projectLabel: response.project_label ?? '',
			projectSourceUrl: this.parseProjectSourcePageUrl(
				response.project_id ?? -1
			),
			elementLabel: response.element_label ?? '',
			elementType: this.getMetadataLabelByType(elementType),
			elementSourceUrl: response.element_label
				? DcRouteUrlBuilder.buildEditPageUrl(
						response.project_id ?? -1,
						response.element_id,
						elementType
				  )
				: '',
			status: response.status, // TO FIX ELSE
			executionTime: response.execution_time
				? dayjs.duration(response.execution_time)
				: null,
			executionDate: response.start_date ? new Date(response.start_date) : null,
			submissionDate: response.submission_date
				? new Date(response.submission_date)
				: null,
			user: response.user_id ?? null,
			errorMessage: response.error_message ?? null,
		});
	}

	private getTaskColumns(): TaskListEntity {
		let availableCols: Array<ITaskListExtra> = [
			{
				field: 'user',
				alias: $localize`:i18n=@@task.list.column.user:`,
				type: 'string',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: false,
				visible: true,
				visibleIndex: 0,
				width: '5.5rem',
			},
			{
				field: 'taskType',
				alias: $localize`:i18n=@@task.list.column.type:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: true,
				visibleIndex: 1,
			},
			{
				field: 'projectLabel',
				alias: $localize`:i18n=@@task.list.column.project:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: true,
				visibleIndex: 2,
			},
			{
				field: 'elementLabel',
				alias: $localize`:i18n=@@task.list.column.element:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: true,
				visibleIndex: 3,
			},
			{
				field: 'elementType',
				alias: $localize`:i18n=@@task.list.column.elementType:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: true,
				visibleIndex: 4,
			},
			{
				field: 'status',
				alias: $localize`:i18n=@@task.list.column.status:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: true,
				visibleIndex: 5,
			},
			{
				field: 'executionTime',
				alias: $localize`:i18n=@@task.list.column.executionTime:`,
				type: 'string',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: false,
				visible: true,
				visibleIndex: 6,
			},
			{
				field: 'executionDate',
				alias: $localize`:i18n=@@task.list.column.executionDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: true,
				visibleIndex: 7,
			},
			{
				field: 'submissionDate',
				alias: $localize`:i18n=@@task.list.column.submissionDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: true,
				visibleIndex: 8,
			},
		];
		availableCols = this.dxListsStorage.synchronize<
			ITaskListExtra,
			TaskItemEntity
		>(DcDxGrids.TaskListStandalone, availableCols);
		return TaskListEntity.build(availableCols);
	}

	private getMetadataLabelByType(type: DcCoreElementType): string {
		switch (type) {
			case DcCoreElementType.DASHBOARD:
				return $localize`:i18n=@@metadata.type.dashboard:`;
			case DcCoreElementType.MAP:
				return $localize`:i18n=@@metadata.type.map:`;
			case DcCoreElementType.CHART:
				return $localize`:i18n=@@metadata.type.chart:`;
			case DcCoreElementType.GRID:
				return $localize`:i18n=@@metadata.type.grid:`;
			case DcCoreElementType.TIMELINE:
				return $localize`:i18n=@@metadata.type.timeline:`;
			case DcCoreElementType.MEDIA:
				return $localize`:i18n=@@metadata.type.media:`;
			case DcCoreElementType.CONNECTOR:
				return $localize`:i18n=@@metadata.type.connector:`;
			case DcCoreElementType.ENTITY:
				return $localize`:i18n=@@metadata.type.entity:`;
			case DcCoreElementType.DATABLOCK:
				return $localize`:i18n=@@metadata.type.datablock:`;
			case DcCoreElementType.DEPOT:
				return $localize`:i18n=@@metadata.type.depot:`;
			case DcCoreElementType.EXPORT_TEMPLATE:
				return $localize`:i18n=@@metadata.type.exportTemplate:`;
			case DcCoreElementType.EXPOSITION:
				return $localize`:i18n=@@metadata.type.exposition:`;
			case DcCoreElementType.RANKING:
				return $localize`:i18n=@@metadata.type.ranking:`;
			default:
				return '';
		}
	}
}
